import React, {useEffect, useRef, useState} from 'react';
import {coursesListText} from '../coursesListText';
import Tooltip from '../../../../uiToolkit/Tooltip';
import {APP_PERMISSIONS} from '../../../../data/permissions';
import {useNavigate, useParams} from 'react-router-dom';
import KebabButton from '../../../../uiToolkit/KebabButton';
import {Icon} from '../../../../uiToolkit/Icon/Icon';

interface CourseCardProps {
  courseId: string;
  courseImage: string;
  logoTextWhite: string;
  courseTitle: string;
  courseSubjecttitle: string;
  coursePublished: boolean;
  courseGradable: boolean;
  courseIsPublic: boolean;
  courseAverageGrade: number;
  courseEnrollNumber: number;
  courseLearners: number;
  onOpenFolders: (courseId: string) => void;
  onPreview: (courseId: string) => void;
  onCopyCourse: (courseId: string) => void;
  onDelete: (courseId: string) => void;
  onEdit: (courseId: string) => void;
  onMoveToProject?: (courseId: string) => void;
  hideProject?: boolean;
  permissions: string[];
  course: any;
}

const NewCourseCard: React.FC<CourseCardProps> = ({
  courseId,
  courseImage,
  logoTextWhite,
  courseTitle,
  courseSubjecttitle,
  coursePublished,
  courseGradable,
  courseIsPublic,
  courseAverageGrade,
  courseEnrollNumber,
  onOpenFolders,
  onPreview,
  onCopyCourse,
  onDelete,
  onEdit,
  onMoveToProject,
  hideProject,
  permissions,
  course,
}) => {
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const menuRef = useRef<any>(null);
  const navigate = useNavigate();
  const {organizationId} = useParams();

  const handleBodyClick = (ev: MouseEvent) => {
    if (activeMenu && menuRef.current && !menuRef.current.contains(ev.target)) {
      setActiveMenu(null);
    }
  };

  useEffect(() => {
    if (activeMenu) {
      window.addEventListener('click', handleBodyClick);
    } else {
      window.removeEventListener('click', handleBodyClick);
    }
    return () => window.removeEventListener('click', handleBodyClick);
  }, [activeMenu]);

  const openMenu = (courseId: string) => {
    setActiveMenu(courseId);
  };

  return (
    <li key={courseId} className="CourseCard col-xl-3 col-lg-4">
      <div>
        {courseImage ? (
          <div
            className="courseCardImage"
            style={{
              backgroundImage: `url(${courseImage})`,
              backgroundSize: courseImage ? 'cover' : '100px',
            }}
          />
        ) : (
          <div className="courseCardImage">
            <img src={logoTextWhite} />
          </div>
        )}
        <div className="CategoryContainer">{courseSubjecttitle}</div>
        <div className="courseCardTitleContainer">
          <div className="courseCardTitleInfo">
            {!hideProject && course.projectName && (
              <p className="CourseCardProject">
                <Icon name="projects-menu.svg" />
                <span>{course.projectName}</span>
              </p>
            )}
            <Tooltip tooltipText={courseTitle}>
              <h1 className="CourseCardTitle">{courseTitle}</h1>
            </Tooltip>
          </div>
          {!courseIsPublic ? (
            <span className="icon-home-line"></span>
          ) : (
            <span className="icon-world"></span>
          )}
        </div>
        <div className="courseCardInfo">
          <div className="courseCardInfoLeftBox">
            {courseGradable ? (
              <>
                <div>
                  <span className="icon-average_score"></span>
                  <p>
                    {coursePublished
                      ? Number.isNaN(courseAverageGrade) ||
                        courseAverageGrade == null
                        ? '-'
                        : Math.round(courseAverageGrade) + '%'
                      : '-'}
                  </p>
                </div>
                <span>{coursesListText.averageScore}</span>
              </>
            ) : (
              <>
                <div
                  style={{
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}>
                  <span className="icon-player"></span>
                </div>
                <span>{coursesListText.participatory}</span>
              </>
            )}
          </div>
          <div className="spacer">
            <span></span>
          </div>
          <div className="courseCardInfoRightBox">
            {coursePublished ? (
              <>
                <div
                  onClick={
                    courseEnrollNumber > 0
                      ? () => {
                          navigate(`/manage/${organizationId}/learners`, {
                            state: {
                              filters: [course.publishedCourseId || courseId],
                              fromPage: 'courses',
                            },
                          });
                        }
                      : undefined
                  }
                  style={{
                    cursor: courseEnrollNumber > 0 ? 'pointer' : 'default',
                  }}>
                  <span className="icon-users"></span>
                  <p>{courseEnrollNumber}</p>
                </div>
                <span>{coursesListText.learners}</span>
              </>
            ) : (
              <>
                <div
                  style={{
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}>
                  <span className="icon-unvisible"></span>
                </div>
                <span>{coursesListText.unpublished}</span>
              </>
            )}
          </div>
        </div>
        <KebabButton
          className="courseCardActions"
          isOpen={activeMenu === courseId}
          onClick={() => openMenu(courseId)}
          ref={menuRef}
        />
        {activeMenu === courseId && (
          <ul className="CourseActionsMenu">
            <li>
              <button onClick={() => onOpenFolders(courseId)}>
                {coursesListText.courseEditor}
              </button>
            </li>
            <li>
              <button onClick={() => onEdit(courseId)}>
                {coursesListText.editInfo}
              </button>
            </li>
            {onMoveToProject &&
              permissions.includes(APP_PERMISSIONS.ORG_CREATE_COURSES) && (
                <li>
                  <button onClick={() => onMoveToProject(courseId)}>
                    {coursesListText.moveToProject}
                  </button>
                </li>
              )}
            <li>
              <button
                onClick={() =>
                  navigate(
                    `/manage/${organizationId}/courses/${courseId}/learners`,
                  )
                }>
                {coursesListText.enrollLearners}
              </button>
            </li>
            <li>
              <button onClick={() => onPreview(courseId)}>
                {coursesListText.viewSyllabus}
              </button>
            </li>
            {permissions.includes(APP_PERMISSIONS.ORG_CREATE_COURSES) && (
              <li>
                <button onClick={() => onCopyCourse(courseId)}>
                  {coursesListText.copy}
                </button>
              </li>
            )}
            {permissions.includes(APP_PERMISSIONS.ORG_DELETE_COURSE) &&
            course.enrollNumber === 0 ? (
              <li>
                <button onClick={() => onDelete(courseId)}>
                  {coursesListText.delete}
                </button>
              </li>
            ) : (
              <li>
                <Tooltip
                  tooltipText={
                    course.enrollNumber > 0
                      ? coursesListText.cantDeleteEnrollments
                      : coursesListText.cantDelete
                  }>
                  <button
                    onClick={() => onDelete(courseId)}
                    disabled
                    className="DeleteDisabled">
                    {coursesListText.delete}
                  </button>
                </Tooltip>
              </li>
            )}
          </ul>
        )}
      </div>
    </li>
  );
};

export default NewCourseCard;
